/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useToast } from '@/contexts/ToastContext'
import {
  invalidateAdvisoryPopups,
  useAdvisoryFeedbackStatus,
  useAdvisoryPopupSettings,
  useAdvisoryPopups,
  useDeepDiveChatMutation,
  useHidePopupMutation,
  useMarkAllPopupsReadMutation,
  useMarkPopupReadMutation,
  useMarkPopupsSeenMutation,
  useAnswerTicketMutation,
  useCustomerTicketThread,
  useRaiseTicketMutation,
  useAdvisoryAiOrgs,
  useAdvisorySupportTickets,
  useHelpArticles,
  useSubmitFeedbackMutation,
  useUpdatePopupSettingMutation,
} from '@/features/advisory-ai/hooks'
import {
  isQueuedDeepDiveResponse,
  type DeepDiveChatMessage,
} from '@/features/advisory-ai/types'
import { feedbackErrorMessage } from '@/api/advisoryFeedback'
import {
  isTicketReference,
  replyErrorMessage,
  startsWithFormulaCharacter,
  supportErrorMessage,
} from '@/api/advisorySupport'
import { GUIDES, SUPPORT_AREAS } from './mockData'
import { isRetainedPopup } from './popupBehaviour'
import { isWelcomePopup } from './types'
import type {
  Guide,
  PopupSetting,
  PopupTrigger,
  PopupMessage,
  AssistantTabKey,
  FeatureVerdict,
  FeedbackDraft,
  GuideScope,
  HelpArticle,
  AssistantSuggestion,
  AssistantSuggestions,
  PreppyMood,
  SupportDraft,
  SupportTicket,
  TicketPlatformOption,
  TicketReply,
  WalkChoice,
  WalkParams,
  WalkStep,
  WalkthroughState,
} from './types'
import { SAMPLE_CALL_ID, isSampleCall } from '@/features/advisory-ai/sampleCall'

/**
 * Every piece of in-progress work in the assistant lives here rather than in
 * the tab that renders it.
 *
 * That is deliberate: tab panels unmount when you switch away, and state held
 * inside them would take a half-written support ticket or an unsent chat
 * message with it. Hoisting the drafts means switching tabs is free.
 */

export type ChatMessage = {
  id: string
  role: 'user' | 'assistant'
  content: string
  pending?: boolean
}

const PENDING_PLACEHOLDER_CONTENT = 'Working on it — updating the report…'
const COMPLETION_CONTENT = 'Done — your changes have been applied to the report.'
const ERROR_CONTENT = 'Sorry, something went wrong updating the report. Please try again.'

export const CHAT_SUGGESTIONS = [
  'What are the key risks?',
  'Draft meeting talking points',
  'Explain the main findings',
]

const EMPTY_FEEDBACK: FeedbackDraft = {
  rating: 0,
  text: '',
  contactOk: true,
  verdicts: {},
  detailOpen: false,
}

/** How long a cheer or a wince lasts before Preppy settles back. */
const REACTION_MS = 2600

/**
 * A pop-up with buttons waits for you; everything else times out.
 *
 * Only `signup` has them — offering a tour is its whole point — and that is
 * recognised by `eventKey` rather than by id, because ids are server Guids now.
 */
const popupHasActions = isWelcomePopup

/** Spec §1: everything without buttons clears itself after 5.2 seconds. */
const POPUP_AUTO_DISMISS_MS = 5200

/** Below the page header actions, which the dock used to sit on top of. */
const DEFAULT_DOCK_TOP = 200

/**
 * `id:updatedAt` of every awaiting ticket already announced, so a second reply
 * on the same ticket pops again. Module scope, not a ref: the provider remounts
 * each time the user comes back into AdvicePrep, and one bubble per reply per
 * page load is the promise. A reload reminds them once more.
 */
const notifiedAwaitingTickets = new Set<string>()

const emptySupportDraft = (): SupportDraft => ({
  subject: '',
  area: SUPPORT_AREAS[0] ?? '',
  body: '',
  platformId: null,
})

/** Same filter the organisation list uses, so the picker is a cache read there. */
const XERO_ACCOUNTING_TYPE_ID = 1

/** Sent as `raisedFrom`. The form only opens from the Support and Feedback tabs, so the screen is the useful signal. */
const RAISED_FROM_BY_SCOPE: Record<GuideScope, string> = {
  'org-list': 'Organisation list',
  calls: 'Meetings',
  prep: 'Meeting prep',
  consolidated: 'Consolidated analysis',
  settings: 'Settings',
}

export type SupportNotice = {
  /** Null when it covers several tickets. */
  ticketId: string | null
  title: string
  body: string
}

type AssistantState = {
  open: boolean
  activeTab: AssistantTabKey
  mood: PreppyMood
  /** Which screen Preppy is sitting on — decides which walkthroughs can run. */
  scope: GuideScope
  /** False when there is no open call, so there is no analysis to read. */
  hasAnalysis: boolean
  orgName: string

  messages: ChatMessage[]
  chatDraft: string
  chatSending: boolean
  awaitingReportUpdate: boolean

  /** What Preppy can open for you when there is no call in context. */
  suggestions: AssistantSuggestions

  popups: PopupMessage[]
  popupSettings: PopupSetting[]
  popupFilter: 'all' | 'unread'
  popupSettingsOpen: boolean
  unreadCount: number
  /** The message currently popping in the corner, if any. */
  activePopup: PopupMessage | null
  /** Support replied and is waiting on the user. Pops after any server pop-up, in the same bubble. */
  supportNotice: SupportNotice | null
  /**
   * Where Preppy is sitting on the right edge. Shared state because the pop-up
   * bubble has to keep its tail on Preppy's face as you drag the dock, and
   * inline `top` changes are not something an observer can report.
   */
  dockTop: number

  guideQuery: string
  openGuideId: string | null
  /** Which help article is open in the reader dialog, if any. */
  openArticleSlug: string | null
  tourSeen: boolean

  /** Help Centre articles, from Intercom via the proxy. */
  articles: HelpArticle[]
  articlesLoading: boolean
  articlesFailed: boolean

  supportView: 'list' | 'form' | 'ticket'
  supportDraft: SupportDraft
  tickets: SupportTicket[]
  /** True on the first load only — a refetch must not blank the list. */
  ticketsLoading: boolean
  ticketsFailed: boolean
  /** Anything not `closed`, straight from the server. Drives the tab subtitle. */
  activeTicketCount: number
  /** Support has replied and is waiting on the user. The email only links back, so the app flags it too. */
  awaitingTicketCount: number
  activeTicketId: string | null
  /** The open ticket's thread, both sides, oldest first. */
  thread: TicketReply[]
  threadLoading: boolean
  threadFailed: boolean
  /** Unsent answers, by ticket, so switching tabs or tickets does not lose one. */
  ticketAnswers: Record<string, string>
  /** The ticket whose answer is being sent, so only that ticket's box locks. */
  ticketAnswerSendingId: string | null
  /** True while a ticket is being raised. */
  ticketSending: boolean
  /** The organisations a ticket can be about. Only fetched while the form is open. */
  ticketPlatforms: TicketPlatformOption[]
  ticketPlatformsLoading: boolean
  ticketPlatformsFailed: boolean
  /** What the ticket will be sent against: the user's pick, else the organisation on screen. */
  ticketPlatformId: string | null

  feedback: FeedbackDraft
  /** True while a submission is in flight. */
  feedbackSending: boolean
  /**
   * Whether this user has ever rated, and what they last said.
   *
   * Server-side and not firm-scoped, so it survives a reload and a change of
   * firm — which is what makes the panel's "asked once" promise true.
   */
  feedbackStatus: { hasRated: boolean; rating: number | null; ratedAt: string | null } | null

  walk: WalkthroughState | null
  /** Anchor the running walkthrough is pointing at, if any. */
  walkAnchor: string | null
  /** Position among the steps this user can run, not raw step indices. */
  walkProgress: { current: number; total: number; isLast: boolean } | null
  /** True while an interactive step waits for the user or the server. */
  walkWaiting: boolean
  /** Whether the open report has been analysed. Gates the guides that need one. */
  analysisReady: boolean
  /** True on the tour's sample prep, where nothing can be saved. */
  onSample: boolean

  /**
   * A walkthrough asking the prep wizard to move before it points at a step.
   *
   * The assistant sits above the router outlet, so it cannot reach into a
   * page's state — it publishes the request and the prep page applies it. The
   * token exists so that asking for the same step twice still fires; a bare
   * number would be swallowed as "no change". It stands for as long as the
   * walkthrough that made it, and is cleared when that ends.
   */
  requestedStep: { step: number; token: number } | null

  /** Bumped whenever a chat reply changed the report, for the prep page to observe. */
  findingsUpdatedToken: number
}

type AssistantActions = {
  setOpen: (open: boolean) => void
  openTab: (tab: AssistantTabKey) => void
  /** Show a short-lived reaction. It expires on its own — moods are not stored. */
  react: (mood: PreppyMood) => void

  setChatDraft: (value: string) => void
  sendMessage: (text: string) => Promise<void>
  /** Navigate to a suggestion; the analysis chat picks up whatever it lands on. */
  openSuggestion: (suggestion: AssistantSuggestion) => void
  /** Navigate to a plain route — what a pop-up's `target` resolves to. */
  openRoute: (to: string) => void

  setPopupFilter: (filter: 'all' | 'unread') => void
  setPopupSettingsOpen: (open: boolean) => void
  markPopupRead: (id: string) => void
  markAllPopupsRead: () => void
  hidePopup: (id: string) => void
  togglePopupSetting: (key: PopupTrigger) => void
  /** Queue messages to pop in the corner, one at a time. */
  enqueuePopups: (items: PopupMessage[]) => void
  /** Close the one currently popping and let the next through. */
  dismissPopup: () => void
  /** Open the panel on the history, marking the popping message read. */
  openActivePopup: () => void
  /** Open the panel on the ticket the notice is about, or the list when it covers several. */
  openSupportNotice: () => void
  dismissSupportNotice: () => void
  setDockTop: (top: number) => void
  /** The prep wizard reporting its current step, for interactive walkthroughs. */
  reportWizardStep: (step: number | null) => void
  /** The overlay reporting that the spotlit element was clicked. */
  notifyAnchorClicked: () => void
  /** The findings editor reporting that an edit was saved. */
  notifyFindingsSaved: () => void
  takeTour: () => void
  declineTour: () => void

  setGuideQuery: (value: string) => void
  setOpenGuideId: (id: string | null) => void
  setOpenArticleSlug: (slug: string | null) => void

  startWalk: (guideId: string) => void
  walkTo: (index: number) => void
  /** Answer a branching step — jumps to the step its choice names. */
  walkChoose: (choice: WalkChoice) => void
  endWalk: (finished: boolean) => void

  setSupportView: (view: 'list' | 'form' | 'ticket') => void
  patchSupportDraft: (patch: Partial<SupportDraft>) => void
  raiseTicket: (subject?: string) => void
  submitTicket: () => void
  openTicketDetail: (id: string) => void
  setTicketAnswer: (ticketId: string, text: string) => void
  sendTicketAnswer: (ticketId: string) => void

  patchFeedback: (patch: Partial<FeedbackDraft>) => void
  sendFeedback: () => void
}

type AssistantContextValue = {
  state: AssistantState
  actions: AssistantActions
}

const AssistantContext = createContext<AssistantContextValue | null>(null)

export function useAssistant() {
  const ctx = useContext(AssistantContext)
  if (!ctx) throw new Error('useAssistant must be used inside AssistantProvider')
  return ctx
}

type AssistantProviderProps = {
  /** Controlled by the host, which owns the single open/closed state. */
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Which screen this instance sits on. Filters the walkthroughs on offer. */
  scope: GuideScope
  clientId: string
  /** Absent outside a prep — the analysis chat is scoped to one call. */
  callId?: string
  /**
   * The organisation (`companyId`) this screen is about, if any. Unlike
   * `walkParams.orgId` it is never a stand-in, because a ticket sent against a
   * stand-in would give support the wrong connection.
   */
  orgId?: string
  orgName?: string
  reportStatus?: string | null
  /** What to offer when there is no call in context. */
  suggestions?: AssistantSuggestions
  /** Asks the layout to reveal the sidebar, for steps that point at it. */
  /** Reveals the sidebar, and expands the section holding `navKey` if given. */
  onRevealNav?: (navKey?: string) => void
  /** What :orgId and :callId resolve to in a walkthrough route. */
  walkParams?: WalkParams
  /** `undefined` while unknown, which is not the same as none. */
  hasMeeting?: boolean | undefined
  children: ReactNode
}

const NO_SUGGESTIONS: AssistantSuggestions = { items: [], loading: false, intro: '', note: '' }
const NO_WALK_PARAMS: WalkParams = { orgId: '', callId: '' }

export function AssistantProvider({
  open,
  onOpenChange,
  scope,
  clientId,
  callId,
  orgId = '',
  orgName,
  reportStatus,
  suggestions = NO_SUGGESTIONS,
  walkParams = NO_WALK_PARAMS,
  hasMeeting,
  onRevealNav,
  children,
}: AssistantProviderProps) {
  const { addToast } = useToast()
  const navigate = useNavigate()

  const hasAnalysis = Boolean(callId)
  const onSample = isSampleCall(callId)

  const [requestedStep, setRequestedStep] = useState<{ step: number; token: number } | null>(null)
  const [findingsUpdatedToken, setFindingsUpdatedToken] = useState(0)
  /**
   * The prep wizard reporting where it actually is.
   *
   * The step channel runs the other way — the assistant asks the page to move —
   * so an interactive step that waits for the user to press Continue needs the
   * page to say when it did. Null off a prep screen.
   */
  const [wizardStep, setWizardStep] = useState<number | null>(null)
  // walkTo is defined further down but the auto-advance effect above needs it;
  // a ref keeps that one-way reference from becoming a declaration order puzzle.
  const walkToRef = useRef<(index: number) => void>(() => {})
  const startWalkRef = useRef<(guideId: string) => boolean>(() => false)

  const requestStep = useCallback((step: number) => {
    setRequestedStep((prev) => ({ step, token: (prev?.token ?? 0) + 1 }))
  }, [])

  // Analysis lands first when it has something to do: a call to read, or — on
  // the list screens — organisations, meetings or consolidated analyses to
  // offer. Everywhere else Guides is the honest default rather than a
  // capability that cannot do anything.
  const [activeTab, setActiveTab] = useState<AssistantTabKey>(
    hasAnalysis || scope === 'calls' || scope === 'org-list' || scope === 'consolidated'
      ? 'analysis'
      : 'guides'
  )

  /**
   * Mood is DERIVED, not set.
   *
   * It used to be imperative state, and moods got stuck: navigating from the
   * organisation list to a meetings list left Preppy thinking forever, because
   * the only thing clearing `think` was a change of callId — and that journey
   * does not change one. Opening the support form had the same problem.
   *
   * So `think` and `point` are now computed from whether Preppy is actually
   * working or actually walking you through something, and cannot lie. Only
   * reactions — a cheer, a wince — get set, and they expire on their own.
   */
  const [reaction, setReaction] = useState<PreppyMood | null>(null)
  const reactionTimer = useRef<number | undefined>(undefined)

  const react = useCallback((next: PreppyMood) => {
    setReaction(next)
    window.clearTimeout(reactionTimer.current)
    reactionTimer.current = window.setTimeout(() => setReaction(null), REACTION_MS)
  }, [])

  useEffect(() => () => window.clearTimeout(reactionTimer.current), [])

  // The greeting is NOT seeded into the thread. Preppy now mounts above the
  // router outlet, so orgName arrives after mount — a baked-in message would be
  // stuck saying "this call" forever. AnalysisTab renders it live instead, and
  // an empty thread is also what tells it to show the suggestion chips.
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [chatDraft, setChatDraft] = useState('')
  const [pendingPlaceholderId, setPendingPlaceholderId] = useState<string | null>(null)
  const sawInProgressRef = useRef(false)

  // Live feed. The GET also records the AdvicePrep visit that the `inactivity`
  // events are computed from, which is why it is mounted here — the provider
  // only exists on AdvicePrep routes — and not somewhere more convenient.
  const popupsQuery = useAdvisoryPopups(clientId)
  const popupSettingsQuery = useAdvisoryPopupSettings(clientId)
  // `mutate` is referentially stable; the mutation object is not, so only the
  // former goes in dependency arrays or the whole context value churns.
  const { mutate: markReadMutate } = useMarkPopupReadMutation(clientId)
  const { mutate: markAllReadMutate } = useMarkAllPopupsReadMutation(clientId)
  const { mutate: hidePopupMutate } = useHidePopupMutation(clientId)
  const { mutate: markSeenMutate } = useMarkPopupsSeenMutation(clientId)
  const { mutate: updateSettingMutate } = useUpdatePopupSettingMutation(clientId)
  const { mutate: submitFeedbackMutate, isPending: feedbackSending } = useSubmitFeedbackMutation(clientId)
  const feedbackStatusQuery = useAdvisoryFeedbackStatus(clientId)

  const popups = useMemo(() => popupsQuery.data?.popups ?? [], [popupsQuery.data])
  // The corner queue pops everything; only these survive in the tab afterwards.
  const retainedPopups = useMemo(() => popups.filter(isRetainedPopup), [popups])
  const popupSettings = useMemo(() => popupSettingsQuery.data ?? [], [popupSettingsQuery.data])

  const [popupFilter, setPopupFilter] = useState<'all' | 'unread'>('all')
  const [popupSettingsOpen, setPopupSettingsOpen] = useState(false)
  const [popupQueue, setPopupQueue] = useState<PopupMessage[]>([])
  const [activePopup, setActivePopup] = useState<PopupMessage | null>(null)
  const [supportNotice, setSupportNotice] = useState<SupportNotice | null>(null)
  const [dockTop, setDockTop] = useState(DEFAULT_DOCK_TOP)
  const popupTimer = useRef<number | undefined>(undefined)
  /** Ids already popped or deleted, so a poll cannot re-enqueue them. */
  const shownRef = useRef<Set<string>>(new Set())

  /**
   * Whether anyone is actually looking.
   *
   * The feed polls on a hidden tab, so without this a pop-up arriving behind one
   * pops, reports itself seen and times out to nobody — and `seen` is exactly
   * what stops it popping again, so an ephemeral message is then gone for good.
   * Bubbles wait for an audience; the queue does not.
   */
  const [documentVisible, setDocumentVisible] = useState(
    () => document.visibilityState === 'visible'
  )

  /**
   * The commit in which the panel opened, computed in render because both the
   * release effect and the clearing effect below need it. Ordering the two
   * effects instead would let the release shift a pop-up off the queue in the
   * very commit that clears it — shown to nobody, and not re-enqueued until the
   * feed content changes.
   */
  const wasOpenRef = useRef(open)
  const justOpened = open && !wasOpenRef.current

  useEffect(() => {
    const onVisibilityChange = () => setDocumentVisible(document.visibilityState === 'visible')
    // Re-read before subscribing: visibility can change between the initialiser
    // running and this effect committing, and the lost direction is the bad one
    // — the queue would stay blocked with nobody left to unblock it.
    onVisibilityChange()
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [])

  const [guideQuery, setGuideQuery] = useState('')
  const [openGuideId, setOpenGuideId] = useState<string | null>(null)
  // Held here, not in the tab: the reader is a dialog over the whole app, so it
  // must survive the panel being closed behind it.
  const [openArticleSlug, setOpenArticleSlug] = useState<string | null>(null)
  /** The Help Centre feed (PREPPY-HELP-SPEC.md §3). */
  const articlesQuery = useHelpArticles(clientId, open)
  const articles = useMemo(() => articlesQuery.data ?? [], [articlesQuery.data])

  // In memory, so it resets on reload. The storage question is now settled —
  // server-side, on the profile initialise response (PREPPY-POPUPS-SPEC.md §7) —
  // and this local flag comes out once that field exists. It is one bit: "has
  // dismissed or completed the tour", distinct from having been offered it,
  // which the popups feed already answers.
  const [tourSeen, setTourSeen] = useState(false)

  const [supportView, setSupportView] = useState<'list' | 'form' | 'ticket'>('list')
  const [supportDraft, setSupportDraft] = useState<SupportDraft>(emptySupportDraft)
  const [activeTicketId, setActiveTicketId] = useState<string | null>(null)
  const [ticketAnswers, setTicketAnswers] = useState<Record<string, string>>({})

  // Panel closed too: the dock badge and the corner bubble are how a user learns support replied.
  const ticketsQuery = useAdvisorySupportTickets(clientId)
  const { mutate: raiseTicketMutate, isPending: ticketSending } = useRaiseTicketMutation(clientId)
  const {
    mutate: answerTicketMutate,
    isPending: answerPending,
    variables: answerVariables,
  } = useAnswerTicketMutation(clientId)
  const ticketAnswerSendingId = answerPending ? answerVariables?.ticketId ?? null : null

  const tickets = useMemo(() => ticketsQuery.data?.tickets ?? [], [ticketsQuery.data])
  const awaitingTicketCount = useMemo(() => tickets.filter((t) => t.status === 'awaiting').length, [tickets])

  const threadQuery = useCustomerTicketThread(
    activeTicketId ?? '',
    clientId,
    open && supportView === 'ticket',
    tickets.find((t) => t.id === activeTicketId)?.updatedAt ?? ''
  )
  const thread = useMemo(() => threadQuery.data ?? [], [threadQuery.data])

  // The firm's own list, not `bas/ui/detail`: that looks the org up by
  // CompanyId across every firm, and the server refuses another firm's platform.
  const ticketOrgsQuery = useAdvisoryAiOrgs({
    clientId: supportView === 'form' ? clientId : '',
    companyName: '',
    typeId: XERO_ACCOUNTING_TYPE_ID,
  })
  const ticketPlatforms = useMemo<TicketPlatformOption[]>(
    () =>
      (ticketOrgsQuery.data ?? [])
        .filter((row) => row.id)
        .map((row) => ({
          platformId: row.id,
          companyId: row.companyId,
          label: row.companyName || row.name,
        })),
    [ticketOrgsQuery.data]
  )
  const screenPlatformId = orgId
    ? ticketPlatforms.find((option) => option.companyId === orgId)?.platformId ?? ''
    : ''
  // A pick only counts while it is one of this firm's: the provider outlives a firm switch,
  // and the server refuses another firm's platform.
  const pickedGeneral = supportDraft.platformId === ''
  const pickedPlatformId = ticketPlatforms.some((option) => option.platformId === supportDraft.platformId)
    ? supportDraft.platformId
    : ''
  /**
   * `null` only distinguishes "has not touched the picker" from "chose None /
   * General" — needed so an untouched picker can still prefill from the screen,
   * while an explicit `''` does NOT fall back and re-attach the very organisation
   * the user said the ticket was not about. Both send no platformId.
   */
  const ticketPlatformId: string | null = pickedGeneral
    ? ''
    : pickedPlatformId || screenPlatformId || null
  const ticketPlatformIdRef = useRef(ticketPlatformId)
  ticketPlatformIdRef.current = ticketPlatformId

  const [feedback, setFeedback] = useState<FeedbackDraft>(EMPTY_FEEDBACK)

  const [walk, setWalk] = useState<WalkthroughState | null>(null)
  /**
   * The step whose anchor has been clicked, for `advanceWhen: 'anchorClicked'`.
   * An index rather than a boolean so a click cannot satisfy a later step.
   */
  const [walkClickedIndex, setWalkClickedIndex] = useState<number | null>(null)
  const [walkSavedIndex, setWalkSavedIndex] = useState<number | null>(null)

  // Safe to instantiate without a call — it is a mutation, so nothing is sent
  // until sendMessage fires, and sendMessage refuses when there is no call.
  // Destructured, not held whole: useMutation returns a fresh object literal
  // each render, so depending on it would rebuild `actions` and the context
  // value every time — and this provider re-renders on each 3s report poll.
  const onFindingsUpdated = useCallback(() => setFindingsUpdatedToken((n) => n + 1), [])
  const { mutateAsync: sendDeepDive, isPending: chatSending } = useDeepDiveChatMutation(
    callId ?? '',
    clientId,
    { onFindingsUpdated }
  )
  const awaitingReportUpdate = pendingPlaceholderId !== null

  const popupSettingOn = useCallback(
    (trigger: PopupTrigger) => popupSettings.find((s) => s.key === trigger)?.on ?? true,
    [popupSettings]
  )

  // Counted locally rather than taken from the response: the server counts the
  // whole feed, which includes the pop-ups that never reach the tab, so its
  // figure would send you looking for rows that are not there.
  const unreadCount = useMemo(() => retainedPopups.filter((p) => p.unread).length, [retainedPopups])

  /**
   * Analysis completion is the one pop-up event with no mutation to hang off:
   * it finishes asynchronously in the AI-worker repo (spec §11), so nothing in
   * this app returns from it.
   *
   * But the report query already polls at 3s while the status is non-terminal,
   * so this app *sees* the completion within a few seconds — it just has no
   * reason to tell the feed. Without this, findings appear on screen and "Your
   * first analysis is ready" turns up as much as three minutes later, which is
   * the most conspicuous version of the polling lag in the product.
   */
  const queryClient = useQueryClient()
  const lastReportStatusRef = useRef<string | null>(null)

  useEffect(() => {
    const status = String(reportStatus || '').toUpperCase()
    const previous = lastReportStatusRef.current
    lastReportStatusRef.current = status || null
    if (!status || !previous || previous === status) return
    if (status === 'COMPLETED_BRIEF' || status === 'COMPLETED_EXPORT') {
      invalidateAdvisoryPopups(queryClient, clientId)
    }
  }, [clientId, queryClient, reportStatus])

  /* ── chat ─────────────────────────────────────────────────────────────── */

  // Resolve the pending placeholder once the worker leaves CHAT_IN_PROGRESS,
  // but only after we have actually observed CHAT_IN_PROGRESS — otherwise the
  // previous terminal status would resolve it instantly. Lives here rather than
  // in the tab so it keeps working while you are on Guides or Support.
  useEffect(() => {
    if (!pendingPlaceholderId) return
    const status = String(reportStatus || '').toUpperCase()

    if (status === 'CHAT_IN_PROGRESS') {
      sawInProgressRef.current = true
      return
    }
    if (!sawInProgressRef.current) return

    const finalContent = status === 'CHAT_ERROR' ? ERROR_CONTENT : COMPLETION_CONTENT
    setMessages((prev) => [
      ...prev.map((m) => (m.id === pendingPlaceholderId ? { ...m, pending: false } : m)),
      { id: `done-${Date.now()}`, role: 'assistant' as const, content: finalContent },
    ])
    setPendingPlaceholderId(null)
    sawInProgressRef.current = false
    react(status === 'CHAT_ERROR' ? 'worry' : 'happy')
  }, [pendingPlaceholderId, react, reportStatus])

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim()
      if (!trimmed || !hasAnalysis || chatSending || awaitingReportUpdate) return

      setActiveTab('analysis')
      setMessages((prev) => [...prev, { id: `user-${Date.now()}`, role: 'user', content: trimmed }])
      setChatDraft('')

      // History for the API excludes unresolved placeholders.
      const historyForApi: DeepDiveChatMessage[] = messages
        .filter((m) => !m.pending)
        .map((m) => ({ role: m.role, content: m.content }))
      historyForApi.push({ role: 'user', content: trimmed })

      try {
        const response = await sendDeepDive({ question: trimmed, messages: historyForApi })

        if (isQueuedDeepDiveResponse(response)) {
          const placeholderId = `pending-${Date.now()}`
          setMessages((prev) => [
            ...prev,
            { id: placeholderId, role: 'assistant', content: PENDING_PLACEHOLDER_CONTENT, pending: true },
          ])
          sawInProgressRef.current = false
          setPendingPlaceholderId(placeholderId)
          return
        }

        const { message } = response

        if (message.intent === 'update') {
          const placeholderContent = message.content?.trim() || PENDING_PLACEHOLDER_CONTENT
          setMessages((prev) => [
            ...prev,
            { id: message.messageId, role: 'assistant', content: placeholderContent, pending: true },
          ])
          sawInProgressRef.current = false
          setPendingPlaceholderId(message.messageId)
          return
        }

        setMessages((prev) => [...prev, { id: message.messageId, role: 'assistant', content: message.content }])
      } catch (error) {
        setMessages((prev) => [
          ...prev,
          {
            id: `error-${Date.now()}`,
            role: 'assistant',
            content: `Sorry, something went wrong. ${error instanceof Error ? error.message : 'Please try again.'}`,
          },
        ])
        react('worry')
      }
    },
    [awaitingReportUpdate, chatSending, hasAnalysis, messages, react, sendDeepDive]
  )

  // A thread belongs to one call. Preppy now outlives navigation, so moving to
  // a different prep has to clear it — otherwise the previous conversation gets
  // sent as history for the wrong report.
  const lastCallIdRef = useRef(callId)
  useEffect(() => {
    if (lastCallIdRef.current === callId) return
    lastCallIdRef.current = callId
    setMessages([])
    setChatDraft('')
    setPendingPlaceholderId(null)
    sawInProgressRef.current = false
  }, [callId])

  /**
   * Open one of Preppy's suggestions.
   *
   * Navigation is the whole mechanism: arriving at the new route re-derives the
   * scope and ids from the URL, so landing on a prep hands the assistant that
   * callId and the analysis chat is live against the report the page is loading.
   */
  const openSuggestion = useCallback(
    (suggestion: AssistantSuggestion) => {
      setActiveTab('analysis')
      onOpenChange(true)
      navigate(suggestion.to)
    },
    [navigate, onOpenChange]
  )

  /**
   * Go where a pop-up points.
   *
   * The panel stays open on purpose — you clicked an entry in the history, so
   * the history is still the thing you were doing.
   */
  const openRoute = useCallback((to: string) => navigate(to), [navigate])

  /* ── panel navigation ─────────────────────────────────────────────────── */

  const setOpen = useCallback((next: boolean) => onOpenChange(next), [onOpenChange])

  const openTab = useCallback(
    (tab: AssistantTabKey) => {
      setActiveTab(tab)
      onOpenChange(true)
    },
    [onOpenChange]
  )

  /* ── popups ───────────────────────────────────────────────────────────── */

  // All three apply optimistically inside the mutation, so the badge and the
  // toggles move the instant you act rather than on the next poll.
  const markPopupRead = useCallback(
    (id: string) => markReadMutate(id),
    [markReadMutate]
  )

  const markAllPopupsRead = useCallback(
    () => markAllReadMutate(),
    [markAllReadMutate]
  )

  const hidePopup = useCallback(
    (id: string) => {
      shownRef.current.add(id)
      setPopupQueue((prev) => prev.filter((p) => p.id !== id))
      hidePopupMutate(id, {
        onError: () => addToast('Could not delete that pop-up — it is back in the list', 'error'),
      })
    },
    [addToast, hidePopupMutate]
  )

  const togglePopupSetting = useCallback(
    (key: PopupTrigger) => {
      const current = popupSettings.find((s) => s.key === key)
      if (!current) return
      updateSettingMutate({ trigger: key, enabled: !current.on })
    },
    [popupSettings, updateSettingMutate]
  )

  /**
   * Proactive pop-ups.
   *
   * Every trigger pops in the corner as it fires, one at a time; the Pop-ups tab
   * is the history of what already popped. A message with buttons waits for you.
   * Everything else times out and lets the next one through.
   */
  const enqueuePopups = useCallback(
    (items: PopupMessage[]) => {
      setPopupQueue((prev) => {
        // Dedup against what is already waiting — the same trigger firing twice
        // should not queue the same message twice.
        const pending = new Set(prev.map((p) => p.id))
        const fresh = items.filter((p) => popupSettingOn(p.trigger) && !pending.has(p.id))
        return fresh.length ? [...prev, ...fresh] : prev
      })
    },
    [popupSettingOn]
  )

  const dismissPopup = useCallback(() => setActivePopup(null), [])

  // Let the next message through once the corner is free. The bubble pops with
  // the panel open too, moving clear of it. Only a walkthrough holds one back,
  // since a bubble over a guided step competes with what the step points at.
  useEffect(() => {
    if (justOpened || !documentVisible || walk || activePopup || popupQueue.length === 0) return
    const [next, ...rest] = popupQueue
    setPopupQueue(rest)
    setActivePopup(next ?? null)
  }, [activePopup, documentVisible, justOpened, popupQueue, walk])

  /**
   * Anything the server has not seen pop yet goes into the queue.
   *
   * Burst control is server-side — the list endpoint marks everything past the
   * newest three as already seen — so whatever arrives unseen is what should
   * actually interrupt. `shownRef` guards the window between a bubble appearing
   * and its receipt landing, where a poll would otherwise re-enqueue it.
   */
  useEffect(() => {
    const unseen = popups.filter((p) => !p.seen && !shownRef.current.has(p.id))
    if (unseen.length) enqueuePopups(unseen)
  }, [enqueuePopups, popups])

  // What happens once, when a bubble appears.
  useEffect(() => {
    if (!activePopup) return
    react('happy')

    // Tell the server it actually appeared. Fire-and-forget: a failed receipt
    // just means it pops again later, which beats blocking the queue on it.
    if (!shownRef.current.has(activePopup.id)) {
      shownRef.current.add(activePopup.id)
      markSeenMutate([activePopup.id])
    }
  }, [activePopup, markSeenMutate, react])

  /**
   * The countdown, kept apart from the cheer above so that visibility can drive
   * it without re-firing everything else — alt-tabbing would otherwise make
   * Preppy cheer again on every return.
   *
   * It does not run on a hidden tab: it would expire against an empty room. It
   * restarts in full rather than resuming, so a bubble you tabbed away from gets
   * its whole five seconds when you come back.
   */
  useEffect(() => {
    window.clearTimeout(popupTimer.current)
    if (!activePopup || popupHasActions(activePopup) || !documentVisible) return
    popupTimer.current = window.setTimeout(() => setActivePopup(null), POPUP_AUTO_DISMISS_MS)
    return () => window.clearTimeout(popupTimer.current)
  }, [activePopup, documentVisible])

  /**
   * Opening the panel consumes whatever is already in the corner — reaching for
   * Preppy is the same gesture as reading the bubble, and the entry is in the
   * tab either way.
   *
   * The transition only, not `open` itself: a pop-up that arrives *later* still
   * pops beside the open panel. Without this an action-bearing bubble opened
   * from the dock had nothing to clear it — those have no auto-dismiss, and only
   * clicking the bubble clears it — so it sat there beside its own entry.
   *
   * Left unread deliberately. It has been seen, not read.
   */
  useEffect(() => {
    wasOpenRef.current = open
    if (justOpened) {
      setActivePopup(null)
      setSupportNotice(null)
    }
  }, [justOpened, open])

  useEffect(() => {
    if (!walk || !activePopup) return
    setPopupQueue((prev) =>
      prev.some((p) => p.id === activePopup.id) ? prev : [activePopup, ...prev]
    )
    setActivePopup(null)
  }, [activePopup, walk])

  useEffect(() => () => window.clearTimeout(popupTimer.current), [])

  const openActivePopup = useCallback(() => {
    if (activePopup) markPopupRead(activePopup.id)
    // Consumed by the tab now. Nothing else clears it — a bubble with buttons
    // has no auto-dismiss, so it would otherwise sit there over its own entry.
    setActivePopup(null)
    setActiveTab('popups')
    onOpenChange(true)
  }, [activePopup, markPopupRead, onOpenChange])

  /* ── walkthrough ──────────────────────────────────────────────────────── */

  /**
   * Fills `:orgId` / `:callId` from whatever the user actually has open, or the
   * first thing available to them.
   *
   * Returns null when a placeholder cannot be filled — a brand-new user with no
   * meetings yet, for instance. The caller skips that step rather than flying to
   * a route that will 404 or landing on an anchor that never mounts.
   */
  const resolveRoute = useCallback(
    (route: string | undefined, sample = false): string | null | undefined => {
      if (!route) return undefined

      // Each placeholder is checked before it is substituted. Blindly calling
      // .replace() with an empty value produced a route that *looked* valid —
      // `/adviceprep/consolidated/` — which then found no anchor and reported
      // itself as a missing element rather than an unrunnable step.
      let filled = route
      if (filled.includes(':orgId')) {
        if (!walkParams.orgId) return null
        filled = filled.replace(':orgId', walkParams.orgId)
      }
      if (filled.includes(':callId')) {

        const target = sample ? SAMPLE_CALL_ID : walkParams.callId
        if (!target) return null
        filled = filled.replace(':callId', target)
      }
      return filled
    },
    [walkParams.callId, walkParams.orgId]
  )

  /** Applies a step's route and wizard position. False means "cannot run this one". */
  const enterStep = useCallback(
    (step: WalkStep, sample = false): boolean => {
      const route = resolveRoute(step.route, sample)
      if (route === null) return false

      const navigated = Boolean(route) && route !== window.location.pathname
      if (navigated && route) navigate(route)
      if (step.step !== undefined) requestStep(step.step)

      // A step pointing into the sidebar needs it open AND the right section
      // expanded — a collapsed section keeps its links in the DOM with no box,
      // which the spotlight reads as a missing anchor. Deferred past the
      // navigation because the layout closes the mobile drawer on route change.
      if (step.anchor.startsWith('nav-')) {
        const navKey = step.anchor.slice('nav-'.length)
        if (navigated) window.setTimeout(() => onRevealNav?.(navKey), 0)
        else onRevealNav?.(navKey)
      }
      return true
    },
    [navigate, onRevealNav, requestStep, resolveRoute]
  )

  /**
   * Has the analysis actually been generated for the open report?
   *
   * Steps that point at findings need this. Generating takes minutes on the
   * server, so those steps are skipped rather than waited on — the spotlight's
   * loading tolerance is for a page still fetching, not for work that has not
   * been done yet.
   */
  const analysisReady = useMemo(() => {
    const status = String(reportStatus || '').toUpperCase()
    return status === 'COMPLETED_BRIEF' || status === 'COMPLETED_EXPORT'
  }, [reportStatus])

  const canRunStep = useCallback(
    (step: WalkStep, sample = false) => {
      if (step.onlyWhen === 'noMeeting' && hasMeeting !== false) return false
      if (resolveRoute(step.route, sample) === null) return false
      if (step.needsAnalysis && !analysisReady) return false
      return true
    },
    [analysisReady, hasMeeting, resolveRoute]
  )

  /** First runnable step at or after `from`, in the given direction. */
  const findRunnableStep = useCallback(
    (guide: Guide, from: number, direction: 1 | -1, sample = false): number => {
      for (let i = from; i >= 0 && i < guide.steps.length; i += direction) {
        const step = guide.steps[i]
        if (step && canRunStep(step, sample)) return i
      }
      return -1
    },
    [canRunStep]
  )

  /**
   * Position among the steps this user can actually run, plus whether any
   * remain.
   *
   * Both are DERIVED rather than stored. Counting raw indices would say "step 4
   * of 5" while showing the third thing the user has seen, and a stored `isLast`
   * would go stale the moment an analysis finishes mid-walk and unlocks a step
   * that was skipped when the walkthrough started.
   */
  const walkProgress = useMemo(() => {
    if (!walk) return null
    const guide = GUIDES.find((g) => g.id === walk.guideId)
    if (!guide) return null
    const runnable = guide.steps.reduce<number[]>(
      (acc, step, index) => (canRunStep(step, walk.sample) ? [...acc, index] : acc),
      []
    )
    const position = runnable.indexOf(walk.index)
    if (position !== -1) {
      return {
        current: position + 1,
        total: runnable.length,
        isLast: position === runnable.length - 1,
      }
    }


    const before = runnable.filter((index) => index < walk.index).length
    return {
      current: before + 1,
      total: runnable.length + 1,
      isLast: !runnable.some((index) => index > walk.index),
    }
  }, [canRunStep, walk])

  /**
   * The step's own advance condition, and whether it is satisfied yet.
   *
   * `wizardAdvanced` deliberately watches the wizard's real position rather than
   * re-checking required fields: the page already refuses to continue without a
   * meeting time, or a period and reports. Duplicating that here would be a
   * second copy of the validation to keep in step with the first.
   */
  const currentWalkStep = useMemo(() => {
    if (!walk) return null
    return GUIDES.find((g) => g.id === walk.guideId)?.steps[walk.index] ?? null
  }, [walk])

  const walkWaiting = useMemo(() => {
    const advance = currentWalkStep?.advanceWhen ?? 'click'
    if (advance === 'click' || !currentWalkStep) return false
    if (advance === 'analysisReady') return !analysisReady
    if (advance === 'anchorClicked') return walkClickedIndex !== (walk?.index ?? -1)
    if (advance === 'meetingCreated') return hasMeeting !== true
    if (advance === 'findingsSaved') return walkSavedIndex !== (walk?.index ?? -1)
    // wizardAdvanced — satisfied once the page reports a step past this one.
    const target = currentWalkStep.step ?? 0
    return wizardStep === null || wizardStep <= target
  }, [
    analysisReady,
    currentWalkStep,
    hasMeeting,
    walk?.index,
    walkClickedIndex,
    walkSavedIndex,
    wizardStep,
  ])

  /**
   * Move on by itself once an interactive step's condition is met.
   *
   * Deliberately not instant: a beat after the user presses Continue, or after
   * an analysis lands, so the change registers as something they did rather than
   * the walkthrough leaping ahead of them.
   */
  const walkIndex = walk?.index
  useEffect(() => {
    if (!walk || walkIndex === undefined || !walk.allowAuto) return
    const advance = currentWalkStep?.advanceWhen ?? "click"
    if (advance === "click" || walkWaiting) return
    const timer = window.setTimeout(() => walkToRef.current(walkIndex + 1), 700)
    return () => window.clearTimeout(timer)
  }, [currentWalkStep, walk, walkIndex, walkWaiting])

  /** Lets the prep wizard tell the assistant where it actually is. */
  const reportWizardStep = useCallback((step: number | null) => setWizardStep(step), [])


  /**
   * The overlay reporting that the spotlit element was clicked. It lives there
   * rather than in the page because it already knows which anchor is spotlit,
   * so any step can use this without its own hook-up.
   */
  const notifyAnchorClicked = useCallback(() => {
    setWalkClickedIndex(walk?.index ?? null)
  }, [walk?.index])

  const notifyFindingsSaved = useCallback(() => {
    setWalkSavedIndex(walk?.index ?? null)
  }, [walk?.index])

  /** False when the guide cannot run here, so a hand-off can fall back. */
  const startWalk = useCallback(
    (guideId: string): boolean => {
      const guide = GUIDES.find((g) => g.id === guideId)
      // Refuse a guide that belongs to another screen rather than flying off to
      // hunt for anchors that are not mounted.
      if (!guide || !guide.scopes.includes(scope)) return false
      if (guide.needsAnalysis && !analysisReady) return false
      if (guide.needsRealCall && onSample) return false

      const first = findRunnableStep(guide, 0, 1)
      if (first === -1) return false

      // Tuck the panel away so the app underneath is fully visible.
      onOpenChange(false)
      setWalkClickedIndex(null)
      setWalkSavedIndex(null)
      setRequestedStep(null)
      setWalk({ guideId, index: first, allowAuto: true })
      const step = guide.steps[first]
      if (step) enterStep(step)
      return true
    },
    [analysisReady, enterStep, findRunnableStep, onOpenChange, onSample, scope]
  )

  const endWalk = useCallback(
    (finished: boolean) => {
      const guideId = walk?.guideId
      setWalk(null)
      setRequestedStep(null)
      if (finished) react('happy')
      if (finished && guideId) {
        setTourSeen(true)
        setOpenGuideId(guideId)
        setActiveTab('guides')
        onOpenChange(true)
      }
    },
    [onOpenChange, react, walk?.guideId]
  )

  const walkTo = useCallback(
    (index: number) => {
      if (!walk) return
      const guide = GUIDES.find((g) => g.id === walk.guideId)
      if (!guide) {
        setWalk(null)
        return
      }


      const handOff = guide.steps[walk.index]?.handOff
      if (handOff && index > walk.index && startWalkRef.current(handOff)) return

      // Skip in whichever direction the user was already going, so Next never
      // stalls on a step this user cannot reach, and Back does not either.
      const direction: 1 | -1 = index >= walk.index ? 1 : -1
      const target = findRunnableStep(guide, index, direction, walk.sample)

      // Running off the end forwards is the natural end of the walkthrough —
      // including when the remaining steps were all skipped.
      if (target === -1) {
        if (direction === 1) endWalk(true)
        return
      }

      const next = guide.steps[target]
      if (!next) return
      enterStep(next, walk.sample)
      // Cleared on every move, so stepping Back asks for the click again.
      setWalkClickedIndex(null)
      setWalkSavedIndex(null)
      setWalk({ guideId: walk.guideId, index: target, allowAuto: direction === 1, sample: walk.sample })
    },
    [endWalk, enterStep, findRunnableStep, walk]
  )

  const walkChoose = useCallback(
    (choice: WalkChoice) => {
      if (!walk) return
      const guide = GUIDES.find((g) => g.id === walk.guideId)
      if (!guide) return

      const named = guide.steps.findIndex((s) => s.id === choice.goTo)
      if (named === -1) return

      const sample = choice.sample ?? false
      // The named step may not be runnable for this user — the same guard the
      // other two ways into a step go through. Fall forward rather than parking
      // them on a step that points at nothing.
      const target = findRunnableStep(guide, named, 1, sample)
      const next = target === -1 ? undefined : guide.steps[target]
      if (!next) {
        endWalk(false)
        return
      }

      setWalkClickedIndex(null)
      setWalkSavedIndex(null)
      setWalk({ guideId: walk.guideId, index: target, allowAuto: true, sample })
      enterStep(next, sample)
    },
    [endWalk, enterStep, findRunnableStep, walk]
  )

  walkToRef.current = walkTo
  startWalkRef.current = startWalk

  // The welcome message is now found by eventKey, not a hardcoded id — ids are
  // server Guids and differ per user.
  const welcomePopupId = useMemo(() => popups.find(isWelcomePopup)?.id, [popups])

  // "Show me around" means whichever screen you are actually on.
  const takeTour = useCallback(() => {
    setTourSeen(true)
    // Same reason as declineTour: taking the tour answers the bubble. Without
    // this the walkthrough effect re-queues it, and it pops again when the tour
    // ends — by then `tourSeen` has taken its buttons away, and a welcome
    // pop-up has no auto-dismiss, so it would sit there blocking the queue.
    setActivePopup(null)
    if (welcomePopupId) markPopupRead(welcomePopupId)
    const introGuide = GUIDES.find((g) => g.scopes.includes(scope) && g.category === 'Getting started')
    if (introGuide) startWalk(introGuide.id)
  }, [markPopupRead, scope, startWalk, welcomePopupId])

  const declineTour = useCallback(() => {
    setTourSeen(true)
    // Declining answers the bubble, so it goes. Nothing else would take it: a
    // welcome pop-up has buttons, and those never auto-dismiss.
    setActivePopup(null)
    if (welcomePopupId) markPopupRead(welcomePopupId)
    addToast('No problem — it is in Guides whenever you want it', 'info')
  }, [addToast, markPopupRead, welcomePopupId])

  /* ── support ──────────────────────────────────────────────────────────── */

  const patchSupportDraft = useCallback((patch: Partial<SupportDraft>) => {
    setSupportDraft((prev) => ({ ...prev, ...patch }))
  }, [])

  /**
   * Read by the submit handler, through a ref.
   *
   * Taking the draft as a dependency gives the handler a new identity on every
   * keystroke, which rebuilds the whole `actions` object and re-runs every
   * consumer effect depending on it — that is what used to knock focus out of
   * these fields after each character.
   */
  const supportDraftRef = useRef(supportDraft)
  supportDraftRef.current = supportDraft

  const raiseTicket = useCallback(
    (subject?: string) => {
      setSupportDraft((prev) => ({ ...prev, subject: subject ?? prev.subject }))
      setSupportView('form')
      setActiveTab('support')
      onOpenChange(true)
    },
    [onOpenChange]
  )

  const openTicketDetail = useCallback((id: string) => {
    setActiveTicketId(id)
    setSupportView('ticket')
  }, [])

  /**
   * Raise it (spec §3.1).
   *
   * The draft is cleared on success and never before — it is the only copy of
   * what somebody wrote, and this endpoint refuses submissions for reasons the
   * user can act on. `body` is required as well as `subject`, because the
   * server requires it and a 400 after the fact is a worse way to find out.
   */
  const submitTicket = useCallback(() => {
    const draft = supportDraftRef.current
    const subject = draft.subject.trim()
    const body = draft.body.trim()
    if (!subject) {
      addToast('Give the ticket a subject first', 'warning')
      return
    }
    if (!body) {
      addToast('Tell us what is happening — a line or two is enough', 'warning')
      return
    }
    // A bullet is the common way in; refused server-side with a body httpClient cannot read.
    if (startsWithFormulaCharacter(subject) || startsWithFormulaCharacter(body)) {
      addToast('The subject and description can’t start with =, +, - or @', 'warning')
      return
    }
    // Nothing picked and None / General are the same ticket: one about no single
    // organisation. Both send no platformId, and neither is refused.
    const platformId = ticketPlatformIdRef.current

    raiseTicketMutate(
      {
        subject,
        area: draft.area,
        body,
        ...(platformId ? { platformId } : {}),
        raisedFrom: RAISED_FROM_BY_SCOPE[scope],
      },
      {
        onSuccess: (ticket) => {
          setSupportDraft(emptySupportDraft())
          setActiveTicketId(ticket.id)
          setSupportView('ticket')
          // Asserted, not assumed: without this, submitting a ticket could land
          // the user back on Analysis.
          setActiveTab('support')
          react('happy')
          addToast(`Ticket ${ticket.reference} created`, 'success')
        },
        onError: (error) => {
          react('worry')
          addToast(supportErrorMessage(error), 'error')
        },
      }
    )
  }, [addToast, raiseTicketMutate, react, scope])

  // A ticket reaching `awaiting` means support replied with a question.
  useEffect(() => {
    const fresh = tickets.filter(
      (t) => t.status === 'awaiting' && !notifiedAwaitingTickets.has(`${t.id}:${t.updatedAt}`)
    )
    if (!fresh.length) return
    fresh.forEach((t) => notifiedAwaitingTickets.add(`${t.id}:${t.updatedAt}`))

    const lookingAtIt = (id: string) =>
      open && activeTab === 'support' && supportView === 'ticket' && activeTicketId === id
    const unseen = fresh.filter((t) => !lookingAtIt(t.id))
    const [only] = unseen
    if (!only) return

    setSupportNotice(
      unseen.length === 1
        ? { ticketId: only.id, title: `Support replied on ${only.reference}`, body: `${only.subject} — your turn.` }
        : { ticketId: null, title: `Support replied on ${unseen.length} tickets`, body: 'They are waiting on you.' }
    )
    react('happy')
  }, [activeTab, activeTicketId, open, react, supportView, tickets])

  /** The panel, on the Support tab, at one ticket — or at the list when there is no single one. */
  const openTicketInPanel = useCallback(
    (ticketId: string | null) => {
      setActiveTab('support')
      if (ticketId) {
        setActiveTicketId(ticketId)
        setSupportView('ticket')
      } else {
        setSupportView('list')
      }
      onOpenChange(true)
    },
    [onOpenChange]
  )

  const openSupportNotice = useCallback(() => {
    const ticketId = supportNotice?.ticketId ?? null
    setSupportNotice(null)
    openTicketInPanel(ticketId)
  }, [openTicketInPanel, supportNotice])

  // Reply emails link to `/advisoryai?ticket=DI-4821`, which redirects here with
  // the query intact. The list is the firm in
  // session only, so a miss almost always means the link is for another firm.
  const [searchParams, setSearchParams] = useSearchParams()
  const linkedReference = searchParams.get('ticket')?.trim() ?? ''
  const handledLinkRef = useRef('')
  useEffect(() => {
    if (!linkedReference || handledLinkRef.current === linkedReference) return
    if (!ticketsQuery.isSuccess && !ticketsQuery.isError) return
    handledLinkRef.current = linkedReference

    const linked = tickets.find((t) => t.reference.toLowerCase() === linkedReference.toLowerCase())
    if (!isTicketReference(linkedReference)) {
      // Not echoed: the text is whatever the link says.
      addToast('That ticket link isn’t valid.', 'warning')
    } else if (linked) {
      openTicketInPanel(linked.id)
    } else if (ticketsQuery.isError) {
      addToast(`Could not open ticket ${linkedReference}. Try again in a moment.`, 'error')
    } else {
      addToast('This ticket belongs to another firm. Switch firm to see it.', 'warning')
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete('ticket')
        return next
      },
      { replace: true }
    )
  }, [
    addToast,
    linkedReference,
    openTicketInPanel,
    setSearchParams,
    tickets,
    ticketsQuery.isError,
    ticketsQuery.isSuccess,
  ])

  const dismissSupportNotice = useCallback(() => setSupportNotice(null), [])

  const setTicketAnswer = useCallback((ticketId: string, text: string) => {
    setTicketAnswers((current) => ({ ...current, [ticketId]: text }))
  }, [])

  // Through a ref for the same reason as `supportDraftRef`: typing must not rebuild `actions`.
  const ticketAnswersRef = useRef(ticketAnswers)
  ticketAnswersRef.current = ticketAnswers

  const sendTicketAnswer = useCallback(
    (ticketId: string) => {
      const body = (ticketAnswersRef.current[ticketId] ?? '').trim()
      if (!body) {
        addToast('Write your answer first', 'warning')
        return
      }
      if (startsWithFormulaCharacter(body)) {
        addToast('An answer can’t start with =, +, - or @', 'warning')
        return
      }

      answerTicketMutate(
        { ticketId, body },
        {
          onSuccess: () => {
            setTicketAnswers((current) => ({ ...current, [ticketId]: '' }))
            react('happy')
            addToast('Sent to support', 'success')
          },
          onError: (error) => {
            react('worry')
            addToast(replyErrorMessage(error), 'error')
          },
        }
      )
    },
    [addToast, answerTicketMutate, react]
  )

  /* ── feedback ─────────────────────────────────────────────────────────── */

  const patchFeedback = useCallback((patch: Partial<FeedbackDraft>) => {
    setFeedback((prev) => ({ ...prev, ...patch }))
  }, [])

  /** Read at submit time only — see the note on `supportDraftRef`. */
  const feedbackRef = useRef(feedback)
  feedbackRef.current = feedback

  /**
   * Send it (spec §2.1).
   *
   * The form is only cleared on success. It is the single copy of something a
   * person took the trouble to write, so a failed request must leave it exactly
   * where it was — and the 400s from this endpoint carry a plain-text reason
   * worth showing rather than a generic apology.
   */
  const sendFeedback = useCallback(() => {
    const draft = feedbackRef.current
    const verdicts = Object.entries(draft.verdicts)
      .filter((entry): entry is [string, FeatureVerdict] => Boolean(entry[1]))
      .map(([featureKey, verdict]) => ({ featureKey, verdict }))

    const text = draft.text.trim()
    if (!draft.rating && !text && verdicts.length === 0) {
      addToast('Give us a star or a sentence first', 'warning')
      return
    }

    submitFeedbackMutate(
      {
        rating: draft.rating || undefined,
        text,
        contactOk: draft.contactOk,
        verdicts,
        context: {
          scope,
          ...(walkParams.orgId ? { orgId: walkParams.orgId } : {}),
          ...(callId ? { callId } : {}),
        },
      },
      {
        onSuccess: () => {
          // Keep the rating and the verdicts on screen. Wiping them made it look
          // as though nothing had been sent; `hasRated` from the server is what
          // the tab reads afterwards anyway.
          setFeedback((prev) => ({ ...prev, text: '' }))
          react('happy')
          addToast('Sent — thank you', 'success')
        },
        onError: (error) => {
          react('worry')
          addToast(feedbackErrorMessage(error), 'error')
        },
      }
    )
  }, [addToast, callId, react, scope, submitFeedbackMutate, walkParams.orgId])

  /**
   * Which anchor the walkthrough is currently pointing at.
   *
   * The dock needs this: it hides while a walkthrough runs, because Preppy is
   * visibly flying around instead — except on the tour's closing beat, which
   * points at the dock itself. Without this it would spotlight an invisible
   * element.
   */
  const walkAnchor = useMemo(() => {
    if (!walk) return null
    const guide = GUIDES.find((g) => g.id === walk.guideId)
    return guide?.steps[walk.index]?.anchor ?? null
  }, [walk])

  /* ── assembled value ──────────────────────────────────────────────────── */

  /**
   * A transient reaction wins while it lasts; otherwise Preppy looks like
   * whatever it is genuinely doing. Nothing here can leave it stuck, because
   * nothing here is stored.
   */
  const mood: PreppyMood =
    reaction ??
    (walk ? 'point' : chatSending || awaitingReportUpdate ? 'think' : 'idle')

  const state = useMemo<AssistantState>(
    () => ({
      open,
      activeTab,
      mood,
      scope,
      hasAnalysis,
      orgName: orgName ?? '',
      messages,
      chatDraft,
      chatSending,
      awaitingReportUpdate,
      suggestions,
      // The tab's list, not the feed: the corner pops every event, only the
      // retained ones are kept here. Still no filter by setting — a disabled
      // stage writes no rows at all server-side.
      popups: retainedPopups,
      popupSettings,
      popupFilter,
      popupSettingsOpen,
      activePopup,
      supportNotice,
      dockTop,
      unreadCount,
      guideQuery,
      openGuideId,
      openArticleSlug,
      tourSeen,
      articles,
      articlesLoading: articlesQuery.isLoading,
      articlesFailed: articlesQuery.isError,
      supportView,
      supportDraft,
      tickets,
      // `isLoading` and not `isFetching`: a background refetch must not blank a
      // list the user is reading.
      ticketsLoading: ticketsQuery.isLoading,
      ticketsFailed: ticketsQuery.isError,
      activeTicketCount: ticketsQuery.data?.activeCount ?? 0,
      awaitingTicketCount,
      activeTicketId,
      thread,
      threadLoading: threadQuery.isLoading,
      threadFailed: threadQuery.isError,
      ticketAnswers,
      ticketAnswerSendingId,
      ticketSending,
      ticketPlatforms,
      ticketPlatformsLoading: ticketOrgsQuery.isLoading,
      ticketPlatformsFailed: ticketOrgsQuery.isError,
      ticketPlatformId,
      feedback,
      feedbackSending,
      feedbackStatus: feedbackStatusQuery.data ?? null,
      walk,
      walkAnchor,
      walkProgress,
      walkWaiting,
      analysisReady,
      onSample,
      requestedStep,
      findingsUpdatedToken,
    }),
    [
      open,
      activeTab,
      mood,
      scope,
      hasAnalysis,
      orgName,
      messages,
      chatDraft,
      chatSending,
      awaitingReportUpdate,
      suggestions,
      retainedPopups,
      popupSettings,
      popupFilter,
      popupSettingsOpen,
      activePopup,
      supportNotice,
      dockTop,
      unreadCount,
      guideQuery,
      openGuideId,
      openArticleSlug,
      tourSeen,
      articles,
      articlesQuery.isLoading,
      articlesQuery.isError,
      supportView,
      supportDraft,
      tickets,
      ticketsQuery.isLoading,
      ticketsQuery.isError,
      ticketsQuery.data,
      awaitingTicketCount,
      activeTicketId,
      thread,
      threadQuery.isLoading,
      threadQuery.isError,
      ticketAnswers,
      ticketAnswerSendingId,
      ticketSending,
      ticketPlatforms,
      ticketOrgsQuery.isLoading,
      ticketOrgsQuery.isError,
      ticketPlatformId,
      feedback,
      feedbackSending,
      feedbackStatusQuery.data,
      walk,
      walkAnchor,
      walkProgress,
      walkWaiting,
      analysisReady,
      onSample,
      requestedStep,
      findingsUpdatedToken,
    ]
  )

  const actions = useMemo<AssistantActions>(
    () => ({
      setOpen,
      openTab,
      react,
      setChatDraft,
      sendMessage,
      openSuggestion,
      openRoute,
      setPopupFilter,
      setPopupSettingsOpen,
      markPopupRead,
      markAllPopupsRead,
      hidePopup,
      togglePopupSetting,
      enqueuePopups,
      dismissPopup,
      openActivePopup,
      openSupportNotice,
      dismissSupportNotice,
      setDockTop,
      reportWizardStep,
      notifyAnchorClicked,
      notifyFindingsSaved,
      takeTour,
      declineTour,
      setGuideQuery,
      setOpenGuideId,
      setOpenArticleSlug,
      startWalk,
      walkTo,
      walkChoose,
      endWalk,
      setSupportView,
      patchSupportDraft,
      raiseTicket,
      submitTicket,
      openTicketDetail,
      setTicketAnswer,
      sendTicketAnswer,
      patchFeedback,
      sendFeedback,
    }),
    [
      setOpen,
      openTab,
      react,
      sendMessage,
      openSuggestion,
      openRoute,
      markPopupRead,
      markAllPopupsRead,
      hidePopup,
      togglePopupSetting,
      enqueuePopups,
      dismissPopup,
      openActivePopup,
      openSupportNotice,
      dismissSupportNotice,
      setDockTop,
      reportWizardStep,
      notifyAnchorClicked,
      notifyFindingsSaved,
      takeTour,
      declineTour,
      startWalk,
      walkTo,
      walkChoose,
      endWalk,
      patchSupportDraft,
      raiseTicket,
      submitTicket,
      openTicketDetail,
      setTicketAnswer,
      sendTicketAnswer,
      patchFeedback,
      sendFeedback,
    ]
  )

  const value = useMemo(() => ({ state, actions }), [state, actions])

  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>
}
